
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AnimatedParticles from './AnimatedParticles.tsx';
import { trackTikTokEvent } from '../tiktok.ts';
import { db } from '../firebase.ts';

// Global declaration for Firebase from CDN
declare const firebase: any;

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g opacity="0.8">
      <path d="M8 1.33301L9.12132 5.87834L10.6667 6.99967L9.12132 8.121L8 12.6663L6.87868 8.121L5.33333 6.99967L6.87868 5.87834L8 1.33301Z" stroke="white" strokeWidth="1" />
      <path d="M13.3333 0L14.0404 3.04043L16 3.74744L14.0404 4.45446L13.3333 7.49489L12.6263 4.45446L10.6667 3.74744L12.6263 3.04043L13.3333 0Z" stroke="white" strokeWidth="0.5" />
    </g>
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="9" fill="rgba(139,92,246,0.2)" />
    <path d="M6.5 10L9 12.5L13.5 7.5" stroke="#C084FC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface PricingPlan {
  id: string;
  nameAr: string;
  nameEn: string;
  price: string;
  effectivePrice?: string;
  discountPercent?: number;
  periodAr: string;
  periodEn: string;
  savingAr?: string;
  savingEn?: string;
  recommended?: boolean;
  featuresAr: string[];
  featuresEn: string[];
  icon: string;
  gradient: string;
  badgeAr?: string;
  badgeEn?: string;
  hidden?: boolean;
  disabled?: boolean;
  quotaText?: string;
  planId?: string;
}

const SITE_TO_PLAN_ID: Record<string, string> = {
  weekly: 'TELEGRAM_PRO_WEEKLY',
  monthly: 'TELEGRAM_PRO_MONTHLY',
  three_months: 'TELEGRAM_PRO_3_MONTHS',
  six_months: 'TELEGRAM_PRO_6_MONTHS',
  yearly: 'TELEGRAM_PRO_YEARLY',
};

const PERIODS: Record<number, { ar: string; en: string }> = {
  7: { ar: 'أسبوعياً', en: 'per week' },
  30: { ar: 'شهرياً', en: 'per month' },
  90: { ar: 'لكل 3 أشهر', en: 'per 3 months' },
  180: { ar: 'لكل 6 أشهر', en: 'per 6 months' },
  365: { ar: 'سنوياً', en: 'per year' },
};

const plans: PricingPlan[] = [
  {
    id: 'weekly',
    nameAr: 'الأسبوعية',
    nameEn: 'Weekly',
    price: '49.99',
    effectivePrice: '44.99',
    discountPercent: 10,
    periodAr: 'أسبوعياً',
    periodEn: 'per week',
    recommended: false,
    icon: '⚡',
    gradient: 'from-slate-700 to-slate-600',
    hidden: true,
    disabled: true,
    featuresAr: [
      'تحليلات الأسهم بالذكاء الاصطناعي',
      'تحليل فني يومي',
      'تنبيهات فنية لحظية',
      'دعم عبر الدردشة',
    ],
    featuresEn: [
      'AI Stock Analytics',
      'Daily Technical Analysis',
      'Instant Technical Alerts',
      'Chat Support',
    ],
  },
  {
    id: 'monthly',
    nameAr: 'الشهرية',
    nameEn: 'Monthly',
    price: '149.99',
    effectivePrice: '149.99',
    discountPercent: 0,
    periodAr: 'شهرياً',
    periodEn: 'per month',
    recommended: false,
    icon: '🚀',
    gradient: 'from-violet-600 to-indigo-600',
    hidden: false,
    disabled: false,
    featuresAr: [
      'كل مميزات الباقة الأسبوعية',
      'تحليلات متقدمة وتقارير أعمق',
      'بيانات السوق الفورية',
      'تنبيهات مخصصة للمحفظة',
      'دعم ذو أولوية',
    ],
    featuresEn: [
      'All Weekly Plan Features',
      'Advanced Analytics & Deeper Reports',
      'Real-time Market Data',
      'Custom Portfolio Alerts',
      'Priority Support',
    ],
  },
  {
    id: 'three_months',
    nameAr: '3 أشهر',
    nameEn: '3 Months',
    price: '399.99',
    effectivePrice: '299.99',
    discountPercent: 25,
    periodAr: 'لكل 3 أشهر',
    periodEn: 'per 3 months',
    savingAr: 'وفر 25%',
    savingEn: 'Save 25%',
    recommended: false,
    icon: '💎',
    gradient: 'from-purple-600 to-violet-500',
    hidden: false,
    disabled: false,
    featuresAr: [
      'كل مميزات الباقة الشهرية',
      'سارة AI لتحليل الأسهم',
      'تحليلات عمق السوق المتقدمة',
      'تقارير أسبوعية مفصلة',
      'دعم ذو أولوية قصوى',
    ],
    featuresEn: [
      'All Monthly Plan Features',
      'Sara AI Stock Analysis',
      'Advanced Market Depth Analytics',
      'Weekly Detailed Reports',
      'Top-priority Support',
    ],
  },
  {
    id: 'six_months',
    nameAr: '6 أشهر',
    nameEn: '6 Months',
    price: '699.99',
    effectivePrice: '490.00',
    discountPercent: 30,
    periodAr: 'لكل 6 أشهر',
    periodEn: 'per 6 months',
    savingAr: 'وفر 30%',
    savingEn: 'Save 30%',
    recommended: false,
    icon: '🌟',
    gradient: 'from-fuchsia-600 to-purple-600',
    hidden: false,
    disabled: false,
    featuresAr: [
      'كل مميزات باقة 3 أشهر',
      'تحليل معنويات السوق',
      'محفظة ذكية تلقائية',
      'تقارير شهرية احترافية',
      'مدير حساب مخصص',
    ],
    featuresEn: [
      'All 3-Month Plan Features',
      'Market Sentiment Analysis',
      'Automated Smart Portfolio',
      'Professional Monthly Reports',
      'Dedicated Account Manager',
    ],
  },
  {
    id: 'yearly',
    nameAr: 'السنوية',
    nameEn: 'Yearly',
    price: '1,199.99',
    effectivePrice: '779.99',
    discountPercent: 35,
    periodAr: 'سنوياً',
    periodEn: 'per year',
    savingAr: 'وفر 35%',
    savingEn: 'Save 35%',
    recommended: true,
    icon: '👑',
    gradient: 'from-amber-500 to-orange-500',
    badgeAr: 'الأكثر اشتراكاً',
    badgeEn: 'Most Popular',
    hidden: false,
    disabled: false,
    featuresAr: [
      'كل مميزات باقة 6 أشهر',
      'وصول API كامل',
      'تحليلات مؤسسية متقدمة',
      'أدوات التعاون الجماعي',
      'دعم 24/7 على مدار السنة',
    ],
    featuresEn: [
      'All 6-Month Plan Features',
      'Full API Access',
      'Institutional-grade Analytics',
      'Team Collaboration Tools',
      '24/7 Year-round Support',
    ],
  },
];

// Secondary Firebase app setup for Tawsela plans
let tawselaDb: any = null;
try {
  if (typeof firebase !== 'undefined') {
    const tawselaConfig = {
      apiKey: "AIzaSyBgIJtzB6D7Fwdlm8TYaPdvQaitI1En8zc",
      authDomain: "tawsela-f5c65.firebaseapp.com",
      projectId: "tawsela-f5c65",
      storageBucket: "tawsela-f5c65.appspot.com",
      messagingSenderId: "60024238755",
      appId: "1:60024238755:web:e0b7fc55a4c1ef57480516",
      measurementId: "G-71CY7QP05Q"
    };

    const tawselaApp = firebase.apps.find((app: any) => app.name === 'tawsela')
      || firebase.initializeApp(tawselaConfig, 'tawsela');
    
    tawselaDb = tawselaApp.firestore();
  }
} catch (e) {
  console.warn("Failed to initialize tawselaApp inside Pricing component:", e);
}

// Helper to format dynamic quota text (follows the display mode + fixed token rate set in Sara admin)
const getDynamicQuotaText = (
  quotaText: string,
  planId: string,
  isAr: boolean,
  tokenRate = 0,
  displayMode: 'tokens' | 'questions' | 'both' = 'tokens'
) => {
  const cleanQuota = quotaText.replace(/[⚡⚡]/g, '').trim();
  const numMatch = cleanQuota.match(/\d+([.,]\d+)?/);
  const numberStr = numMatch ? numMatch[0] : '';
  if (!numberStr) return quotaText;

  const count = Number(numberStr.replace(/,/g, '')) || 0;
  const tokens = tokenRate > 0 ? Math.round(count * tokenRate) : 0;
  const qStr = count.toLocaleString('en-US');
  const tStr = tokens.toLocaleString('en-US');
  const isWeekly = planId.includes('WEEKLY');
  const isMonthly = planId.includes('MONTHLY');

  if (isAr) {
    const period = isWeekly ? 'أسبوعياً' : isMonthly ? 'شهرياً' : 'إجمالي';
    if (!tokens || displayMode === 'questions') return `⚡ ${qStr} سؤال للذكاء الاصطناعي ${period}`;
    if (displayMode === 'tokens') return `⚡ ${tStr} توكن للذكاء الاصطناعي ${period}`;
    return `⚡ ${qStr} سؤال ≈ ${tStr} توكن ${period}`;
  }
  const period = isWeekly ? 'per week' : isMonthly ? 'per month' : 'total';
  if (!tokens || displayMode === 'questions') return `⚡ ${qStr} AI Questions ${period}`;
  if (displayMode === 'tokens') return `⚡ ${tStr} AI tokens ${period}`;
  return `⚡ ${qStr} questions ≈ ${tStr} tokens ${period}`;
};

const Pricing: React.FC = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [plansList, setPlansList] = useState<PricingPlan[]>(plans);
  const [tokenConfig, setTokenConfig] = useState<{ rate: number; mode: 'tokens' | 'questions' | 'both' }>({ rate: 0, mode: 'tokens' });

  // Plan cards follow the display mode and fixed token rate set in Sara admin.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('https://sara-chat-worker.itsmybadsoul.workers.dev/api/sara-public/config');
        if (!res.ok) return;
        const cfg = await res.json();
        if (cancelled) return;
        setTokenConfig({
          rate: Number(cfg?.tokensPerQuestion) || 0,
          mode: cfg?.displayMode === 'questions' || cfg?.displayMode === 'both' ? cfg.displayMode : 'tokens',
        });
      } catch { /* keep question-only fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Plans managed from the dashboard (dash.tad-ai.com/#/plans).
    const fetchManagedPlans = async (): Promise<boolean> => {
      const snapshot = await db.collection('plans').get();
      const docs: any[] = [];
      snapshot.forEach((doc: any) => {
        const d = doc.data();
        if (d.type !== 'questions_pack' && doc.id.startsWith('TELEGRAM_')) docs.push({ id: doc.id, ...d });
      });
      if (docs.length === 0) return false;
      docs.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

      const managed = docs.map((d): PricingPlan => {
        const siteId = Object.keys(SITE_TO_PLAN_ID).find(k => SITE_TO_PLAN_ID[k] === d.id);
        const base = plans.find(p => p.id === siteId) || {
          id: d.id,
          nameAr: d.id,
          nameEn: d.id,
          price: '0',
          periodAr: '',
          periodEn: '',
          featuresAr: plans[1].featuresAr,
          featuresEn: plans[1].featuresEn,
          icon: '🚀',
          gradient: 'from-violet-600 to-indigo-600',
        };
        const price = Number(d.price) || 0;
        const discountPct = Number(d.discountPercent) || 0;
        const effective = typeof d.effectivePrice === 'number'
          ? d.effectivePrice
          : Math.round(price * (1 - discountPct / 100) * 100) / 100;
        const period = PERIODS[d.durationDays];
        const customAr = (d.featuresAr || []).filter(Boolean);
        const customEn = (d.featuresEn || []).filter(Boolean);
        return {
          ...base,
          planId: d.id,
          nameAr: d.nameAr || base.nameAr,
          nameEn: d.nameEn || base.nameEn,
          price: price.toFixed(2),
          effectivePrice: effective.toFixed(2),
          discountPercent: discountPct,
          savingAr: discountPct > 0 ? (d.discountLabel || `وفر ${discountPct}%`) : undefined,
          savingEn: discountPct > 0 ? `Save ${discountPct}%` : undefined,
          periodAr: period?.ar || (d.durationDays ? `لكل ${d.durationDays} يوم` : base.periodAr),
          periodEn: period?.en || (d.durationDays ? `per ${d.durationDays} days` : base.periodEn),
          featuresAr: customAr.length > 0 ? ['', ...customAr] : base.featuresAr,
          featuresEn: customEn.length > 0 ? ['', ...customEn] : base.featuresEn,
          recommended: d.popular === true,
          badgeAr: d.popular === true ? 'الأكثر اشتراكاً' : undefined,
          badgeEn: d.popular === true ? 'Most Popular' : undefined,
          hidden: d.hidden === true,
          disabled: d.active === false,
          quotaText: d.saraQuestions > 0 ? String(d.saraQuestions) : '',
        };
      });
      setPlansList(managed);
      return true;
    };

    const fetchPlans = async () => {
      try {
        if (await fetchManagedPlans()) return;
      } catch (err) {
        console.error("Failed to load managed plans:", err);
      }
      try {
        if (!tawselaDb) return;
        const snapshot = await tawselaDb.collection('payment_plans').get();
        const dbPlans: Record<string, any> = {};
        snapshot.forEach((doc: any) => {
          dbPlans[doc.id] = doc.data();
        });

        if (Object.keys(dbPlans).length > 0) {
          const updatedPlans = plans.map(plan => {
            let dbId = '';
            if (plan.id === 'weekly') dbId = 'TELEGRAM_PRO_WEEKLY';
            else if (plan.id === 'monthly') dbId = 'TELEGRAM_PRO_MONTHLY';
            else if (plan.id === 'three_months') dbId = 'TELEGRAM_PRO_3_MONTHS';
            else if (plan.id === 'six_months') dbId = 'TELEGRAM_PRO_6_MONTHS';
            else if (plan.id === 'yearly') dbId = 'TELEGRAM_PRO_YEARLY';

            const dbPlan = dbPlans[dbId];
            if (!dbPlan) return plan;

            const discountPct = typeof dbPlan.discountPercent === 'number' ? dbPlan.discountPercent : 0;
            const originalAmount = typeof dbPlan.amount === 'number' ? dbPlan.amount : parseFloat(plan.price.replace(/,/g, ''));
            const effective = discountPct > 0 
                ? Math.round(originalAmount * (1 - discountPct / 100) * 100) / 100
                : originalAmount;

            const savePctTextAr = discountPct > 0 ? `وفر ${discountPct}%` : undefined;
            const savePctTextEn = discountPct > 0 ? `Save ${discountPct}%` : undefined;

            return {
              ...plan,
              price: originalAmount.toFixed(2),
              effectivePrice: effective.toFixed(2),
              discountPercent: discountPct,
              savingAr: savePctTextAr || plan.savingAr,
              savingEn: savePctTextEn || plan.savingEn,
              hidden: dbPlan.hidden === true,
              disabled: dbPlan.disabled === true,
              quotaText: dbPlan.quotaText || '',
            };
          });
          setPlansList(updatedPlans);
        }
      } catch (err) {
        console.error("Failed to load payment plans dynamically:", err);
      }
    };
    fetchPlans();
  }, []);

  const visiblePlans = plansList.filter(plan => !plan.hidden);

  return (
    <section id="pricing" className="relative z-10 overflow-hidden pt-20 lg:pt-24 xl:pt-28 pb-20 scroll-mt-16 bg-[#060B27]">
      <div className="pricing-section-bg -z-10">
        <AnimatedParticles />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 xl:px-0">
        {/* Header */}
        <div className="text-center mb-16 relative z-10">
          <span className="section-badge relative mb-5 font-semibold text-xs inline-flex items-center gap-2 py-2 px-4.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-400">
            <SparkleIcon />
            <span>{isAr ? 'الاشتراكات والباقات' : 'Subscriptions & Plans'}</span>
          </span>
          <h2 className="text-white mb-4 text-3xl font-black sm:text-4xl xl:text-5xl tracking-tight bg-clip-text bg-gradient-to-b from-white to-white/70">
            {isAr ? 'اختر خطتك الاستثمارية اليوم' : 'Choose Your Investment Plan'}
          </h2>
          <p className="max-w-2xl mx-auto font-medium text-white/60 text-base leading-relaxed">
            {isAr
              ? 'باقات مرنة ومدروسة تناسب جميع المستثمرين والمهتمين بالسوق السعودي. اختر الباقة الملائمة لبدء استخدام التحليلات الفورية وأدوات سارة الذكية.'
              : 'Flexible plans suited for all Saudi market investors and traders. Choose the best package to start receiving real-time insights and analytics.'}
          </p>
        </div>

        {/* Plans Grid */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${visiblePlans.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3 xl:grid-cols-5'} gap-6 items-stretch`}>
          {visiblePlans.map((plan) => {
            const isRecommended = plan.recommended;
            const badge = isAr ? plan.badgeAr : plan.badgeEn;
            const hasDiscount = plan.discountPercent && plan.discountPercent > 0;
            const displayPrice = plan.effectivePrice || plan.price;
            
            // Generate features list (using database quota if available)
            let features = isAr ? [...plan.featuresAr] : [...plan.featuresEn];
            if (plan.quotaText) {
              const mappedQuota = getDynamicQuotaText(
                plan.quotaText,
                plan.planId || SITE_TO_PLAN_ID[plan.id] || plan.id,
                isAr,
                tokenConfig.rate,
                tokenConfig.mode
              );
              if (features.length > 0) {
                features[0] = mappedQuota;
              } else {
                features.push(mappedQuota);
              }
            }
            features = features.filter(Boolean);

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-3xl p-7 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(139,92,246,0.15)] group ${
                  isRecommended
                    ? 'bg-gradient-to-br from-violet-950/40 via-indigo-950/30 to-slate-950/60 border-2 border-violet-500/80 shadow-[0_0_40px_rgba(139,92,246,0.25)]'
                    : 'bg-white/[0.03] backdrop-blur-xl border border-white/5 hover:border-white/15 hover:bg-white/[0.05]'
                }`}
              >
                {/* Popular/Value Badge */}
                {badge && (
                  <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-black tracking-wider uppercase whitespace-nowrap shadow-lg ${
                    isRecommended 
                      ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white' 
                      : 'bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950'
                  }`}>
                    {badge}
                  </div>
                )}

                {/* Saving badge (only if no main badge is active) */}
                {(isAr ? plan.savingAr : plan.savingEn) && !badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] font-black whitespace-nowrap bg-emerald-500/90 text-white shadow-md shadow-emerald-500/20">
                    {isAr ? plan.savingAr : plan.savingEn}
                  </div>
                )}

                {/* Header Info */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="text-3xl filter drop-shadow-md inline-block transform transition-transform duration-300 group-hover:scale-110">{plan.icon}</span>
                    <h3 className="text-xl font-bold text-white mt-2 group-hover:text-violet-400 transition-colors">{isAr ? plan.nameAr : plan.nameEn}</h3>
                  </div>
                  {/* Small discount tag on top right if badges are active */}
                  {hasDiscount && badge && (
                    <span className="text-[10px] font-black text-emerald-400 bg-emerald-400/10 py-1 px-2.5 rounded-full border border-emerald-400/20">
                      {isAr ? plan.savingAr : plan.savingEn}
                    </span>
                  )}
                </div>

                {/* Price block */}
                <div className="mb-7">
                  {hasDiscount ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white/40 line-through font-semibold select-none">
                          {parseFloat(plan.price).toLocaleString()} {isAr ? 'ريال' : 'SAR'}
                        </span>
                        {!badge && (
                          <span className="text-[10px] font-black text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-md border border-emerald-400/10">
                            {isAr ? `وفر ${plan.discountPercent}%` : `${plan.discountPercent}% OFF`}
                          </span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold text-white tracking-tight">{parseFloat(displayPrice).toLocaleString()}</span>
                        <span className="text-sm font-semibold text-white/70">{isAr ? 'ريال' : 'SAR'}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-white tracking-tight">{parseFloat(displayPrice).toLocaleString()}</span>
                      <span className="text-sm font-semibold text-white/70">{isAr ? 'ريال' : 'SAR'}</span>
                    </div>
                  )}
                  <p className="text-[11px] text-white/45 mt-1.5 font-medium uppercase tracking-wider">{isAr ? plan.periodAr : plan.periodEn}</p>
                </div>

                {/* Divider line */}
                <div className={`h-px mb-6 transition-colors duration-300 ${isRecommended ? 'bg-violet-500/20 group-hover:bg-violet-500/35' : 'bg-white/5 group-hover:bg-white/10'}`} />

                {/* Features List */}
                <ul className="space-y-3.5 flex-1">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2.5 rtl:flex-row-reverse">
                      <CheckIcon />
                      <span className="text-white/80 text-[13px] leading-relaxed group-hover:text-white transition-colors">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Action Button */}
                <div className="mt-8 z-10">
                  <a
                    href={`https://dash.tad-ai.com/#/pricing?plan=${plan.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackTikTokEvent('InitiateCheckout', {
                        content_name: isAr ? plan.nameAr : plan.nameEn,
                        content_id: plan.id,
                        value: parseFloat(displayPrice) || 0,
                        currency: 'SAR'
                      });
                    }}
                    className={`block w-full text-center py-3.5 px-4 rounded-xl font-bold text-sm transition-all duration-300 ${
                      isRecommended
                        ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white shadow-lg shadow-violet-600/20 hover:shadow-violet-600/40 hover:-translate-y-0.5'
                        : 'bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-violet-500/30 hover:-translate-y-0.5'
                    }`}
                  >
                    {isAr ? 'اشترك الآن' : 'Subscribe Now'}
                  </a>
                  <p className="text-center text-[10px] text-white/35 mt-2.5">
                    {isAr ? 'إتمام فوري وآمن بنسبة 100%' : '100% Secure & Instant Activation'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footnotes */}
        <div className="text-center mt-16 max-w-2xl mx-auto border-t border-white/5 pt-8">
          <p className="text-white/50 text-sm font-medium">
            {isAr
              ? '🔐 جميع الاشتراكات تشمل الوصول الكامل للمنصة، شات سارة التفاعلي وقناة التنبيهات الفنية اللحظية'
              : '🔐 All subscriptions include full platform access, Sara AI Chat and real-time technical alerts channel'}
          </p>
          <p className="text-white/35 text-xs mt-3">
            {isAr
              ? 'الأسعار بالريال السعودي (SAR) • يمكنك إلغاء تجديد الاشتراك تلقائياً في أي وقت من لوحة التحكم'
              : 'Prices in Saudi Riyals (SAR) • You can cancel subscription renewal at any time from your dashboard'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
